import { ArticleOutlined as ArticleOutlinedIcon } from 'components/icons';
import { useCallback, useMemo } from 'react';

import Loader from 'components/Loader.tsx';
import PermissionButton from 'components/buttons/PermissionButton.tsx';
import { useModal } from 'components/modals/useModal.ts';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { Checkbox } from 'components/ui/checkbox';
import { FilterConfig } from 'components/tables/list-table/ListTableTypes.tsx';
import ListTableWithFilters from 'components/tables/list-table/ListTableWithFilters';
import { FormattedMessage } from 'react-intl';
import {
    useLazyGetDocumentTemplatesWithFiltersQuery,
    useUpdateDocumentTemplateMutation,
} from 'store/api/document-templates.ts';
import { DocumentTemplateDto, DocumentTemplateUpdateDto } from 'types/document-templates.ts';
import { errorAlert } from 'utils/alert';
import { getClassifierText, t } from 'utils/getTranslations';
import { useClassifiers } from 'utils/useClassifiers.ts';
import DocumentTemplateEditModal from './DocumentTemplateEditModal.tsx';

const DocumentTemplatesTable = () => {
    const [fetchData, { data, isFetching }] = useLazyGetDocumentTemplatesWithFiltersQuery();
    const [updateDocumentTemplate] = useUpdateDocumentTemplateMutation();

    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({ codes: ['DOCUMENT_TEMPLATE_TYPE'] });
    const { openModal, closeModal } = useModal();

    const filterConfig: FilterConfig = useMemo(
        () => ({
            quickFilters: [],
            filters: [],
            searchBarSelectors: [{ selector: 'name' }, { selector: 'documentTemplateType' }],
        }),
        []
    );

    const onFormSubmit = useCallback(
        async (formData: DocumentTemplateUpdateDto): Promise<void> => {
            try {
                if (formData) {
                    updateDocumentTemplate({ ...formData })
                        .unwrap()
                        .then(() => {
                            closeModal();
                            fetchData({});
                        })
                        .catch(console.error);
                }
            } catch (e) {
                console.error('onSubmit error: ', e);
                errorAlert(t('forms.error-submitting-the-form'));
            }
        },
        [updateDocumentTemplate, closeModal, fetchData]
    );

    const openEditDocumentTemplateModal = useCallback(
        (editableEntity: DocumentTemplateUpdateDto): void => {
            openModal(
                <DocumentTemplateEditModal
                    documentTemplate={editableEntity}
                    onChange={(data) => {
                        void onFormSubmit(data);
                    }}
                    onClose={closeModal}
                />,
                closeModal
            );
        },
        [onFormSubmit, openModal, closeModal]
    );

    const columns = useMemo<DataTableColumnDef<DocumentTemplateDto>[]>(
        () => [
            {
                header: t('name'),
                id: 'name',
                enableSorting: false,
                meta: {
                    width: '50%',
                    widthWeight: 4,
                    minPx: 300,
                },
                cell: ({ row }) => {
                    const { name } = row.original;
                    return <div className="flex flex-col font-bold">{name ?? '-'}</div>;
                },
            },
            {
                header: t('document-template.document-template-type'),
                accessorKey: 'documentTemplateType',
                enableSorting: false,
                meta: {
                    width: '25%',
                    widthWeight: 2,
                    minPx: 180,
                },
                cell: ({ getValue }) =>
                    classifiers['DOCUMENT_TEMPLATE_TYPE']
                        ? getClassifierText(classifiers['DOCUMENT_TEMPLATE_TYPE'], getValue<string>())
                        : '',
            },
            {
                header: t('document-template.is-default'),
                accessorKey: 'isDefault',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 120,
                },
                cell: ({ getValue }) => (
                    <Checkbox checked={!!getValue<boolean>()} disabled aria-label={t('document-template.is-default')} />
                ),
            },
            {
                header: '',
                id: 'editButton',
                enableSorting: false,
                meta: {
                    fixedPx: 100,
                },
                cell: ({ row }) => {
                    const documentTemplateDto = row.original;
                    return (
                        <div className="flex flex-nowrap items-center justify-end">
                            <PermissionButton
                                permissions={['UPDATE.DOCUMENT_TEMPLATE']}
                                variant="outlined"
                                size="extraSmall"
                                onClick={() => {
                                    openEditDocumentTemplateModal({
                                        id: documentTemplateDto.id,
                                        name: documentTemplateDto.name,
                                        isDefault: documentTemplateDto.isDefault,
                                    });
                                }}
                            >
                                <FormattedMessage id="edit" />
                            </PermissionButton>
                        </div>
                    );
                },
            },
        ],
        [classifiers, openEditDocumentTemplateModal]
    );

    if (isLoadingClassifiers) {
        return <Loader />;
    }

    return (
        <ListTableWithFilters<DocumentTemplateDto>
            // Managed v8 engine for the weighted-width layout; column picker / reorder / resize
            // stay OFF (flags default false). Lives in the Settings tabs layout, which grows to
            // content and never hands the table a bounded scroller — so cap the container height
            // here to make it the virtualizer's scroll element and let row windowing engage.
            columnConfigId="document-templates"
            dataTableClassName="max-h-[calc(100vh-20rem)]"
            columns={columns}
            data={data}
            isLoading={isFetching}
            pluralTextId="document-template.plural"
            singularTextId="document-template.singular"
            noEntriesId="document-template.no-document-template-found"
            noEntriesBodyId="document-template.empty.body"
            noEntriesIcon={ArticleOutlinedIcon}
            rowsPerPage={50}
            fetchData={fetchData}
            filterConfig={filterConfig}
        />
    );
};

export default DocumentTemplatesTable;
